/**
 * S4 — Industry Pack editor (`/ai-studio/packs/:packId`), WF-F2 / DR2.
 *
 * Draft packs (platform admin): planning-style split — pack authoring assist + form.
 * Published packs: read-only full-width form.
 */

import React, { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import { Button } from '@/components/ui/Button';
import { LifecycleBadge } from '@/components/ai-studio/agents';
import {
  ExpectedRowsEditor,
  FIELD_CLASS,
  HINT_CLASS,
  LABEL_CLASS,
  ResourceTypesEditor,
  SECTION_CLASS,
} from '@/components/ai-studio/definition';
import {
  ASSIST_FORM_BODY_CLASS,
  ASSIST_PANE_HEADER_CLASS,
  ASSIST_PANE_SHELL_CLASS,
  ASSIST_PANE_TITLE_CLASS,
  ASSIST_SPLIT_GRID_CLASS,
  ASSIST_WORKSPACE_CONTAINER_CLASS,
  ASSIST_WORKSPACE_PAGE_CLASS,
} from '@/components/ai-studio/assist/assistPaneStyles';
import { PackAuthoringInterview, usePackEditor } from '@/components/ai-studio/packs';
import { JsonPanel } from '@/components/ai-studio/versions';

const AIStudioPackEditorPage: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isPlatformAdmin =
    user?.role === 'Platform Administrator' || user?.role === 'PLATFORM_ADMIN';
  const { packId } = useParams<{ packId: string }>();
  const editor = usePackEditor(packId);
  const [confirmingPublish, setConfirmingPublish] = useState(false);
  const [mobilePane, setMobilePane] = useState<'chat' | 'form'>('chat');

  const { draft, pack, readOnly } = editor;

  const handleConfirmPublish = useCallback(async () => {
    await editor.publish();
    setConfirmingPublish(false);
  }, [editor]);

  const handleApplyAuthoringPatch = useCallback(
    (patch: Parameters<typeof editor.applyAuthoringPatch>[0]) => {
      editor.applyAuthoringPatch(patch);
    },
    [editor]
  );

  if (editor.loading) {
    return (
      <main className="px-4 py-4 md:px-6 lg:px-8">
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.packs.loading')}
        </p>
      </main>
    );
  }

  if (!pack || !draft) {
    return (
      <main className="flex flex-col gap-3 px-4 py-4 md:px-6 lg:px-8">
        <p role="alert" className="text-base text-[var(--color-error)]">
          {t(editor.errorKey ?? 'aiStudio.packs.notFound')}
        </p>
        <Link to="/ai-studio/packs" className="w-fit text-base text-[var(--color-info)] underline">
          {t('aiStudio.packs.backToCatalog')}
        </Link>
      </main>
    );
  }

  const documentKey = `${pack.id}:${pack.updatedAt}`;
  const showAuthoring = isPlatformAdmin && !readOnly;

  const publishConfirmBlock = confirmingPublish && !readOnly && (
    <section className={SECTION_CLASS} aria-label={t('aiStudio.packs.publishConfirmTitle')}>
      <h2 className="text-base font-semibold text-[var(--color-surface-text)]">
        {t('aiStudio.packs.publishConfirmTitle')}
      </h2>
      <p className="mt-2 text-base text-[var(--color-surface-text-secondary)]">
        {t('aiStudio.packs.publishConfirm')}
      </p>
      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={() => void handleConfirmPublish()}
          disabled={editor.publishing || !editor.jsonValid}
          className="rounded-md bg-[var(--color-accent-bg)] px-4 py-2 text-base text-[var(--color-accent-text)] hover:opacity-90 disabled:opacity-50"
        >
          {editor.publishing
            ? t('aiStudio.packs.publishing')
            : t('aiStudio.packs.publishConfirmAction')}
        </button>
        <button
          type="button"
          onClick={() => setConfirmingPublish(false)}
          disabled={editor.publishing}
          className="rounded-md border border-[var(--color-border)] px-4 py-2 text-base text-[var(--color-surface-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50"
        >
          {t('aiStudio.common.cancel')}
        </button>
      </div>
    </section>
  );

  const packFormSections = (
    <>
      {readOnly && (
        <p className="rounded border border-[var(--color-border)] bg-[var(--color-secondary-bg)] px-4 py-2 text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.packs.publishedNotice')}
        </p>
      )}

      {editor.errorKey && (
        <div
          role="alert"
          className="rounded border border-[var(--color-error)] bg-[var(--color-surface-bg)] px-4 py-2 text-base text-[var(--color-error)]"
        >
          {t(editor.errorKey)}
        </div>
      )}

      {publishConfirmBlock}

      <section className={SECTION_CLASS}>
        <h2 className="mb-3 text-base font-semibold text-[var(--color-surface-text)]">
          {t('aiStudio.definition.basicInfo')}
        </h2>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className={LABEL_CLASS} htmlFor="pack-editor-code">
              {t('aiStudio.packs.fieldCode')}
            </label>
            <input
              id="pack-editor-code"
              className={FIELD_CLASS}
              value={pack.code}
              disabled
              readOnly
            />
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="pack-editor-name">
              {t('aiStudio.packs.fieldName')}
            </label>
            <input
              id="pack-editor-name"
              className={FIELD_CLASS}
              value={draft.name}
              disabled={readOnly}
              onChange={(event) => editor.setField('name', event.target.value)}
            />
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="pack-editor-domain">
              {t('aiStudio.packs.fieldDomain')}
            </label>
            <input
              id="pack-editor-domain"
              className={FIELD_CLASS}
              value={draft.domain}
              disabled={readOnly}
              onChange={(event) => editor.setField('domain', event.target.value)}
            />
          </div>
          <div className="md:col-span-2 lg:col-span-4">
            <label className={LABEL_CLASS} htmlFor="pack-editor-description">
              {t('aiStudio.definition.description')}
            </label>
            <textarea
              id="pack-editor-description"
              className={FIELD_CLASS}
              rows={2}
              value={draft.description}
              disabled={readOnly}
              onChange={(event) => editor.setField('description', event.target.value)}
            />
          </div>
          <div className="md:col-span-2 lg:col-span-4">
            <label className={LABEL_CLASS} htmlFor="pack-editor-purpose">
              {t('aiStudio.definition.purpose')}
            </label>
            <textarea
              id="pack-editor-purpose"
              className={FIELD_CLASS}
              rows={2}
              value={draft.purpose}
              disabled={readOnly}
              onChange={(event) => editor.setField('purpose', event.target.value)}
            />
          </div>
          <div className="md:col-span-2 lg:col-span-4">
            <label className={LABEL_CLASS} htmlFor="pack-editor-scope">
              {t('aiStudio.definition.scopeHonesty')}
            </label>
            <textarea
              id="pack-editor-scope"
              className={FIELD_CLASS}
              rows={2}
              value={draft.scopeHonesty}
              disabled={readOnly}
              onChange={(event) => editor.setField('scopeHonesty', event.target.value)}
            />
          </div>
          <div className="md:col-span-2 lg:col-span-4">
            <label className={LABEL_CLASS} htmlFor="pack-editor-posture">
              {t('aiStudio.definition.expertPosture')}
            </label>
            <textarea
              id="pack-editor-posture"
              className={FIELD_CLASS}
              rows={3}
              value={draft.expertPosture}
              disabled={readOnly}
              onChange={(event) => editor.setField('expertPosture', event.target.value)}
            />
            <p className={HINT_CLASS}>{t('aiStudio.definition.expertPostureHint')}</p>
          </div>
        </div>
      </section>

      <section className={SECTION_CLASS}>
        <ExpectedRowsEditor
          label={t('aiStudio.definition.appendInputExpected')}
          hint={t('aiStudio.definition.appendInputExpectedHint')}
          kind="input"
          rows={draft.appendInputExpected}
          disabled={readOnly}
          onChange={(rows) => editor.setField('appendInputExpected', rows)}
        />
      </section>

      <section className={SECTION_CLASS}>
        <label className={LABEL_CLASS} htmlFor="pack-append-context">
          {t('aiStudio.definition.appendContext')}
        </label>
        <p className={HINT_CLASS}>{t('aiStudio.definition.appendContextHint')}</p>
        <textarea
          id="pack-append-context"
          className={FIELD_CLASS}
          rows={4}
          value={draft.appendContext}
          disabled={readOnly}
          onChange={(event) => editor.setField('appendContext', event.target.value)}
        />
      </section>

      <section className={SECTION_CLASS}>
        <ExpectedRowsEditor
          label={t('aiStudio.definition.appendOutputExpected')}
          hint={t('aiStudio.definition.appendOutputExpectedHint')}
          kind="output"
          rows={draft.appendOutputExpected}
          disabled={readOnly}
          onChange={(rows) => editor.setField('appendOutputExpected', rows)}
        />
      </section>

      <section className={SECTION_CLASS}>
        <label className={LABEL_CLASS} htmlFor="pack-append-instructions">
          {t('aiStudio.definition.appendInstructions')}
        </label>
        <p className={HINT_CLASS}>{t('aiStudio.definition.appendInstructionsHint')}</p>
        <textarea
          id="pack-append-instructions"
          className={FIELD_CLASS}
          rows={4}
          value={draft.appendInstructions}
          disabled={readOnly}
          onChange={(event) => editor.setField('appendInstructions', event.target.value)}
        />
      </section>

      <section className={SECTION_CLASS}>
        <ResourceTypesEditor
          types={draft.resourceTypes}
          disabled={readOnly}
          onChange={(types) => editor.setField('resourceTypes', types)}
        />
      </section>

      <section className={SECTION_CLASS}>
        <button
          type="button"
          className="text-base font-semibold text-[var(--color-surface-text)] hover:underline"
          aria-expanded={editor.showAdvancedJson}
          onClick={() => editor.setShowAdvancedJson(!editor.showAdvancedJson)}
        >
          {t('aiStudio.definition.advancedJson')}
        </button>
        {editor.showAdvancedJson && (
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <JsonPanel
              label={t('aiStudio.packs.vocabularyMap')}
              hint={t('aiStudio.packs.vocabularyMapHint')}
              value={draft.vocabularyMap}
              documentKey={`${documentKey}:vocabularyMap`}
              disabled={readOnly}
              onChange={(value) =>
                editor.setField('vocabularyMap', value as Record<string, unknown>)
              }
              onValidityChange={(valid) => editor.setJsonValidity('vocabularyMap', valid)}
            />
            <JsonPanel
              label={t('aiStudio.packs.enabledPlugins')}
              hint={t('aiStudio.packs.enabledPluginsHint')}
              value={draft.enabledPlugins}
              documentKey={`${documentKey}:enabledPlugins`}
              rootKind="array"
              disabled={readOnly}
              onChange={(value) => editor.setField('enabledPlugins', value as unknown[])}
              onValidityChange={(valid) => editor.setJsonValidity('enabledPlugins', valid)}
            />
            <JsonPanel
              label={t('aiStudio.packs.defaultWeights')}
              hint={t('aiStudio.packs.defaultWeightsHint')}
              value={draft.defaultWeights}
              documentKey={`${documentKey}:defaultWeights`}
              disabled={readOnly}
              onChange={(value) =>
                editor.setField('defaultWeights', value as Record<string, unknown>)
              }
              onValidityChange={(valid) => editor.setJsonValidity('defaultWeights', valid)}
            />
            <JsonPanel
              label={t('aiStudio.packs.importTemplate')}
              hint={t('aiStudio.packs.importTemplateHint')}
              value={draft.importTemplate}
              documentKey={`${documentKey}:importTemplate`}
              disabled={readOnly}
              onChange={(value) =>
                editor.setField('importTemplate', value as Record<string, unknown>)
              }
              onValidityChange={(valid) => editor.setJsonValidity('importTemplate', valid)}
            />
          </div>
        )}
      </section>
    </>
  );

  if (showAuthoring) {
    return (
      <div className={ASSIST_WORKSPACE_PAGE_CLASS}>
        <div className={ASSIST_WORKSPACE_CONTAINER_CLASS}>
          <div className="mb-3 shrink-0">
            <nav className="mb-2 flex flex-wrap items-center gap-3 text-sm">
              <Link to="/ai-studio/packs" className="text-[var(--color-info)] underline">
                {t('aiStudio.packs.backToCatalog')}
              </Link>
            </nav>
            <h1 className="flex items-center gap-3 text-xl font-bold text-[var(--color-surface-text)]">
              {t('aiStudio.packs.editorTitle', { name: pack.name })}
              <LifecycleBadge status={pack.lifecycleStatus} />
            </h1>
            <p className="text-sm text-[var(--color-secondary-text)]">
              {t('aiStudio.packAuthoring.pageSubtitle')}
            </p>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="mb-2 flex flex-shrink-0 items-center justify-end gap-2 px-1 lg:hidden">
              {mobilePane === 'form' ? (
                <button
                  type="button"
                  onClick={() => setMobilePane('chat')}
                  className="rounded-lg border border-[var(--color-border)] px-3 py-2 text-base"
                >
                  {t('aiStudio.packAuthoring.backToChat')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setMobilePane('form')}
                  className="rounded-lg bg-[var(--color-primary-bg)] px-4 py-2.5 text-base font-medium text-[var(--color-primary-text)]"
                >
                  {t('aiStudio.packAuthoring.formPane')}
                </button>
              )}
            </div>

            <div className={`min-h-0 flex-1 overflow-hidden ${ASSIST_SPLIT_GRID_CLASS}`}>
              <div
                className={`${ASSIST_PANE_SHELL_CLASS} ${
                  mobilePane === 'chat' ? 'flex' : 'hidden'
                } lg:flex lg:h-full`}
              >
                <PackAuthoringInterview
                  packId={pack.id}
                  packName={draft.name}
                  enabled={!editor.saving}
                  workspacePane
                  onApplyPatch={handleApplyAuthoringPatch}
                />
              </div>

              <div
                className={`${ASSIST_PANE_SHELL_CLASS} ${
                  mobilePane === 'form' ? 'flex' : 'hidden'
                } lg:flex lg:h-full`}
              >
                <div className={ASSIST_PANE_HEADER_CLASS}>
                  <h2 className={ASSIST_PANE_TITLE_CLASS}>
                    {t('aiStudio.packAuthoring.formPane')}
                  </h2>
                  <div className="flex shrink-0 gap-2">
                    {!confirmingPublish && (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => void editor.save()}
                          disabled={editor.saving || !editor.dirty || !editor.jsonValid}
                          loading={editor.saving}
                        >
                          {editor.saving ? t('aiStudio.common.saving') : t('aiStudio.common.save')}
                        </Button>
                        <Button
                          type="button"
                          variant="success"
                          size="sm"
                          onClick={() => setConfirmingPublish(true)}
                          disabled={editor.publishing || !editor.jsonValid}
                        >
                          {t('aiStudio.packs.publish')}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                <div className={ASSIST_FORM_BODY_CLASS}>{packFormSections}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <main className="flex w-full flex-col gap-4 px-4 py-4 md:px-6 lg:px-8">
      <nav>
        <Link to="/ai-studio/packs" className="text-base text-[var(--color-info)] underline">
          {t('aiStudio.packs.backToCatalog')}
        </Link>
      </nav>

      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-3 text-lg font-semibold text-[var(--color-surface-text)]">
          {t('aiStudio.packs.editorTitle', { name: pack.name })}
          <LifecycleBadge status={pack.lifecycleStatus} />
        </h1>
        {!readOnly && !confirmingPublish && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void editor.save()}
              disabled={editor.saving || !editor.dirty || !editor.jsonValid}
              className="rounded-md border border-[var(--color-border)] px-4 py-2 text-base text-[var(--color-surface-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50"
            >
              {editor.saving ? t('aiStudio.common.saving') : t('aiStudio.common.save')}
            </button>
            <button
              type="button"
              onClick={() => setConfirmingPublish(true)}
              disabled={editor.publishing || !editor.jsonValid}
              className="rounded-md bg-[var(--color-accent-bg)] px-4 py-2 text-base text-[var(--color-accent-text)] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] disabled:opacity-50"
            >
              {t('aiStudio.packs.publish')}
            </button>
          </div>
        )}
      </header>

      {packFormSections}
    </main>
  );
};

export default AIStudioPackEditorPage;
